import type { LicensedProducts } from '@atlassian/jira-shared-types/src/tenant-context.tsx';
import { hasProductAccess } from '../permission-check/index.tsx';
import { fetchIsRequestAccessCapable } from '../request-capabilities/index.tsx';
import { isSite7DaysOld } from '../site-eligibility/index.tsx';
import { isSiteOptedOut } from '../site-opt-out/index.tsx';
import { isNewActiveUser } from '../user-eligibility/index.tsx';

export type JpdCJNIneligibilityReason =
	| 'NEW_ACTIVE_USER'
	| 'NEW_SITE_LESS_THAN_7_DAYS_OLD'
	| 'ALREADY_HAVE_ACCESS'
	| 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS'
	| 'NO_JPD_LICENCE'
	| 'SITE_OPTED_OUT';

export type JpdCJNEligibilityCheckInput = {
	cloudId: string;
	userId: string;
	licensedProducts: LicensedProducts;
};

export type JpdCJNEligibilityCheckResult =
	| {
			eligible: false;
			ineligibilityReason: JpdCJNIneligibilityReason;
	  }
	| { eligible: true };

export const checkJpdCrossJoinNudgeEligibility = async ({
	cloudId,
	userId,
	licensedProducts,
}: JpdCJNEligibilityCheckInput): Promise<JpdCJNEligibilityCheckResult> => {
	if (!licensedProducts['jira-product-discovery']) {
		return {
			eligible: false,
			ineligibilityReason: 'NO_JPD_LICENCE',
		};
	}

	if (await isSiteOptedOut(cloudId)) {
		return {
			eligible: false,
			ineligibilityReason: 'SITE_OPTED_OUT',
		};
	}

	if (!(await isSite7DaysOld('jira-product-discovery', cloudId))) {
		return {
			eligible: false,
			ineligibilityReason: 'NEW_SITE_LESS_THAN_7_DAYS_OLD',
		};
	}

	if (await isNewActiveUser(cloudId, userId)) {
		return {
			eligible: false,
			ineligibilityReason: 'NEW_ACTIVE_USER',
		};
	}

	if (await hasProductAccess('jira-product-discovery', cloudId)) {
		return {
			eligible: false,
			ineligibilityReason: 'ALREADY_HAVE_ACCESS',
		};
	}

	if (!(await fetchIsRequestAccessCapable('jira-product-discovery', cloudId))) {
		return {
			eligible: false,
			ineligibilityReason: 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS',
		};
	}

	return {
		eligible: true,
	};
};
